import { createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = {
  class: "ll-ff-assignment-select"
};
import { computed } from 'vue';
import LlSelect from '@/components/LlSelect.vue';
export default {
  __name: 'FixedFeePicker',
  props: {
    modelValue: {
      type: [Object, null],
      default: null
    },
    /** Array of charge objects { id, description } for the selected matter */
    fixedFeeList: {
      type: Array,
      default: () => []
    },
    /** Whether a client or matter filter is currently active */
    hasMatter: {
      type: Boolean,
      default: false
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
    labelClass: {
      type: String,
      default: ''
    },
    componentClass: {
      type: String,
      default: 'remove-outline'
    },
    placeholder: {
      type: String,
      default: null
    },
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dataCy: {
      type: String,
      default: 'ff-assignment-filter'
    },
    loading: {
      type: Boolean,
      default: false
    },
    /** When false, hides All/Assigned/Unassigned options (legacy charge-only mode) */
    showAssignmentOptions: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(__props, {
    emit: __emit
  }) {
    /**
     * Context-aware Fixed Fee Assignment filter dropdown.
     *
     * Shows assignment status options (All / Assigned / Unassigned) always.
     * When a matter is selected, also shows specific fees from the matter's charge list
     * below a divider. When no matter is selected, shows a helper hint instead.
     *
     * Emits selected value with `itemType` discriminator:
     *   - { label, id, itemType: 'assignment' }  → filter by assignment status
     *   - { label, id, itemType: 'charge' }      → filter by specific charge
     *
     * Maps to backend:
     *   All        → no filter param
     *   Assigned   → fixedFeeAssignment=Assigned
     *   Unassigned → fixedFeeAssignment=Unassigned
     *   Specific   → chargeId={guid}
     */
    const props = __props;
    const emit = __emit;
    const effectiveLabel = computed(() => {
      return props.showAssignmentOptions ? 'Fixed Fee Assignment' : 'Fixed Fee';
    });
    const effectivePlaceholder = computed(() => {
      if (props.placeholder) return props.placeholder;
      return props.showAssignmentOptions ? 'Select Fixed Fee Assignment' : 'Select Fixed Fee';
    });
    const assignmentItems = [{
      label: 'All',
      id: 'All',
      itemType: 'assignment'
    }, {
      label: 'Assigned',
      id: 'Assigned',
      itemType: 'assignment'
    }, {
      label: 'Unassigned',
      id: 'Unassigned',
      itemType: 'assignment'
    }];
    const chargeItems = computed(() => {
      if (!props.hasMatter || props.fixedFeeList.length === 0) {
        return [];
      }
      return props.fixedFeeList.map(fee => ({
        label: fee.description,
        id: fee.id,
        itemType: 'charge'
      }));
    });
    const chargeItemById = computed(() => {
      return new Map(chargeItems.value.map(item => [item.id, item]));
    });

    /**
     * Builds the full items list for v-select.
     * When showAssignmentOptions is true (default), assignment options are included.
     * When a matter with fees is selected, fee items are appended.
     * The #item slot handles rendering the divider/subheader before the first charge item.
     */
    const allItems = computed(() => {
      return props.showAssignmentOptions ? [...assignmentItems, ...chargeItems.value] : chargeItems.value;
    });

    /** ID of the first charge item — used to inject divider before it in the #item slot */
    const firstChargeId = computed(() => {
      return props.fixedFeeList.length > 0 ? props.fixedFeeList[0].id : null;
    });

    /**
     * Keep v-select model aligned with items shape.
     * In legacy mode the parent passes { id, description }, while this component
     * renders items as { id, label, itemType }.
     */
    const normalizedModelValue = computed(() => {
      if (!props.modelValue) {
        return null;
      }
      if (props.showAssignmentOptions) {
        return props.modelValue;
      }
      const existingItem = chargeItemById.value.get(props.modelValue?.id);
      if (existingItem) {
        return existingItem;
      }
      if (props.modelValue?.id && props.modelValue?.description) {
        return {
          id: props.modelValue.id,
          label: props.modelValue.description,
          itemType: 'charge'
        };
      }
      return props.modelValue;
    });

    /** Handle item selection (both assignment options and specific fees) */
    const handleChange = value => {
      if (!props.showAssignmentOptions) {
        if (!value) {
          emit('update:modelValue', null);
          emit('change', null);
          return;
        }
        const legacyChargeValue = {
          id: value.id,
          description: value.label ?? value.description
        };
        emit('update:modelValue', legacyChargeValue);
        emit('change', legacyChargeValue);
        return;
      }
      emit('update:modelValue', value);
      emit('change', value);
    };
    return (_ctx, _cache) => {
      const _component_v_list_item = _resolveComponent("v-list-item");
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_createVNode(LlSelect, {
        "model-value": normalizedModelValue.value,
        items: allItems.value,
        label: __props.hideLabel ? '' : effectiveLabel.value,
        "label-class": __props.labelClass || 'py-1 text-sm',
        placeholder: effectivePlaceholder.value,
        "component-class": __props.componentClass,
        clearable: __props.clearable,
        disabled: __props.disabled,
        "data-cy": __props.dataCy,
        loading: __props.loading,
        "hide-details": "",
        "is-autocomplete": "",
        "onUpdate:modelValue": handleChange
      }, {
        item: _withCtx(({
          item,
          props: itemProps
        }) => [__props.showAssignmentOptions && item.raw.itemType === 'charge' && item.raw.id === firstChargeId.value ? (_openBlock(), _createElementBlock(_Fragment, {
          key: 0
        }, [_cache[0] || (_cache[0] = _createElementVNode("div", {
          class: "my-1 border-t border-gray-300"
        }, null, -1)), _cache[1] || (_cache[1] = _createElementVNode("div", {
          class: "px-4 py-1 text-xs text-gray-500 font-medium uppercase tracking-wide"
        }, " Specific Fees ", -1)), _cache[2] || (_cache[2] = _createElementVNode("div", {
          class: "my-1 border-t border-gray-300"
        }, null, -1))], 64)) : _createCommentVNode("", true), _createVNode(_component_v_list_item, _mergeProps(itemProps, {
          title: item.raw.label
        }), null, 16, ["title"])]),
        "append-item": _withCtx(() => [!__props.hasMatter ? (_openBlock(), _createElementBlock(_Fragment, {
          key: 0
        }, [_cache[3] || (_cache[3] = _createElementVNode("div", {
          class: "my-1 border-t border-gray-300"
        }, null, -1)), _cache[4] || (_cache[4] = _createElementVNode("div", {
          class: "px-4 py-2 text-xs text-gray-400 italic"
        }, " Select a client or matter to filter specific fees ", -1))], 64)) : __props.hasMatter && __props.fixedFeeList.length === 0 && !__props.loading ? (_openBlock(), _createElementBlock(_Fragment, {
          key: 1
        }, [_cache[5] || (_cache[5] = _createElementVNode("div", {
          class: "my-1 border-t border-gray-300"
        }, null, -1)), _cache[6] || (_cache[6] = _createElementVNode("div", {
          class: "px-4 py-2 text-xs text-gray-400 italic"
        }, " No fixed fees on this matter ", -1))], 64)) : _createCommentVNode("", true)]),
        _: 1
      }, 8, ["model-value", "items", "label", "label-class", "placeholder", "component-class", "clearable", "disabled", "data-cy", "loading"])]);
    };
  }
};